/**
 * AdminPreviewBar
 * Pill flotante visible SOLO para admins.
 * Permite alternar entre "Real" (lo que ve admin), "Comercio" (vista B2B)
 * y "Consumidor final" (vista B2C). Persiste la elección en localStorage
 * para que toda la app responda al modo elegido.
 *
 * Cuando hay un modo activo distinto de "Real" se muestra con un acento
 * de color y la opción para volver al modo real.
 *
 * Se oculta automáticamente en rutas /admin* (donde el admin trabaja con
 * sus herramientas) y en /facturador / /banner-render para no estorbar.
 */
import React, { useEffect, useState } from 'react';
import { Eye, X, RefreshCw } from 'lucide-react';

const KEY = 'bebida-bacana-admin-preview-type';

const AdminPreviewBar = () => {
  const [value, setValue] = useState(() =>
    (typeof window !== 'undefined' && localStorage.getItem(KEY)) || 'real'
  );
  const [open, setOpen] = useState(false);
  const [pathname, setPathname] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');

  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    // Track route changes via interval (router-agnostic)
    const id = setInterval(() => {
      if (window.location.pathname !== pathname) setPathname(window.location.pathname);
    }, 500);
    return () => { window.removeEventListener('popstate', onPop); clearInterval(id); };
  }, [pathname]);

  // Solo visible para admin
  const isAdmin =
    typeof window !== 'undefined' &&
    localStorage.getItem('bebida-bacana-admin') === 'true';
  if (!isAdmin) return null;

  // No mostrar en pantallas del admin / render / facturador
  if (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/facturador') ||
    pathname.startsWith('/banner-render') ||
    pathname.startsWith('/catalog-render')
  ) return null;

  const change = (v) => {
    if (v === 'real') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, v);
    setValue(v);
    setOpen(false);
    // Forzar refresh para que TODO se recalcule (precios, filtros, etc)
    window.location.reload();
  };

  const isPreview = value !== 'real';
  const labelByValue = {
    real: 'Vista real (admin)',
    comercio: 'Vista Comercio (B2B)',
    consumidor_final: 'Vista Consumidor final',
  };
  const colorByValue = {
    real: '#64748b',
    comercio: '#0d9488',     // teal
    consumidor_final: '#d97706', // amber
  };

  return (
    <div
      className="fixed z-[9999] bottom-20 sm:bottom-6 right-4 select-none"
      data-testid="admin-preview-bar"
    >
      {open && (
        <div
          data-testid="admin-preview-menu"
          className="absolute bottom-full right-0 mb-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-black uppercase tracking-wider text-slate-500">
              Vista previa Admin
            </span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
              <X size={14} />
            </button>
          </div>
          {['real', 'comercio', 'consumidor_final'].map((v) => (
            <button
              key={v}
              onClick={() => change(v)}
              data-testid={`admin-preview-${v}`}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-slate-50 transition-colors ${
                value === v ? 'font-bold bg-slate-100' : 'text-slate-700'
              }`}
              style={value === v ? { color: colorByValue[v] } : {}}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: colorByValue[v] }}
              />
              {labelByValue[v]}
              {value === v && <span className="ml-auto text-[10px]">●</span>}
            </button>
          ))}
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-500 leading-tight">
            Cambiá la vista para ver los precios y opciones tal como las ven tus clientes.
            La app se va a recargar para aplicar el modo.
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((p) => !p)}
        data-testid="admin-preview-toggle"
        className={`flex items-center gap-2 px-3 py-2 rounded-full shadow-lg border-2 text-xs font-bold transition-all ${
          isPreview ? 'text-white animate-pulse' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
        }`}
        style={isPreview ? { backgroundColor: colorByValue[value], borderColor: colorByValue[value] } : {}}
        title="Cambiar vista previa"
      >
        {isPreview ? <RefreshCw size={14} /> : <Eye size={14} />}
        <span className="hidden sm:inline">{labelByValue[value]}</span>
        <span className="sm:hidden">{value === 'real' ? 'Vista' : value === 'comercio' ? 'B2B' : 'B2C'}</span>
      </button>
    </div>
  );
};

export default AdminPreviewBar;
